import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { sewaKendaraanService } from '../../services/sewaKendaraanService';
import { getAssetUrl } from '../../services/api';
import {
    FaBuilding, FaUser, FaIdCard, FaPhone, FaEnvelope, FaHome,
    FaUpload, FaFilePdf, FaCheckCircle, FaArrowLeft, FaArrowRight,
    FaInfoCircle, FaCalendarCheck, FaClipboardList, FaPrint,
    FaMapMarkedAlt, FaClock, FaFileAlt, FaWifi, FaCar, FaSnowflake,
    FaDesktop, FaChair, FaUsers, FaMusic, FaVideo, FaUtensils,
    FaParking, FaSearch, FaCalendarAlt, FaMoneyBillWave, FaBus,
    FaTruck, FaMotorcycle, FaTools, FaCogs, FaHardHat, FaTractor,
    FaToolbox, FaLaptop, FaCamera, FaMicrophone, FaPrinter,
    FaProjectDiagram, FaChalkboardTeacher, FaSpinner,
    FaChevronLeft, FaChevronRight, FaExclamationTriangle,
    FaMapMarkerAlt, FaImage, FaPaperPlane
} from 'react-icons/fa';

const SewaBMD = () => {
    const [activeTab, setActiveTab] = useState('daftar');
    const [activeCategory, setActiveCategory] = useState('kendaraan');
    const [step, setStep] = useState(1);
    const [showForm, setShowForm] = useState(false);

    // ✅ Kendaraan dari API
    const [kendaraanList, setKendaraanList] = useState([]);
    const [loadingKendaraan, setLoadingKendaraan] = useState(true);
    const [kendaraanError, setKendaraanError] = useState('');

    // ✅ Slider state per kendaraan: { [id]: index }
    const [sliderIndex, setSliderIndex] = useState({});

    // Form state
    const [formData, setFormData] = useState({
        // Pemohon
        namaLengkap: '',
        nik: '',
        noTelp: '',
        email: '',
        alamat: '',
        pekerjaan: '',
        jenisInstansi: 'perseorangan',
        namaInstansi: '',

        // Kendaraan
        kendaraanDipilih: '',
        tujuanPenggunaan: '',
        lokasiTujuan: '',
        jumlahPenumpang: '',
        termasukSupir: false,
        tanggalMulai: '',
        tanggalSelesai: '',

        // Dokumen
        ktp: null,
        sim: null,
        stnk: null,
        suratPernyataan: null,
        dokumenPendukung: null,

        // Honeypot
        website_url: '',
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [nomorRegistrasi, setNomorRegistrasi] = useState('');

    // ============ TABS & CATEGORIES ============
    const tabs = [
        { id: 'daftar', label: 'Daftar Aset', icon: FaCar },
        { id: 'formulir', label: 'Formulir Sewa', icon: FaFileAlt },
        { id: 'status', label: 'Cek Status', icon: FaSearch },
    ];

    const categories = [
        { id: 'kendaraan', label: 'Kendaraan Dinas', icon: FaCar },
    ];

    // ============ LOAD KENDARAAN ============
    useEffect(() => {
        loadKendaraans();
    }, []);

    const loadKendaraans = async () => {
        setLoadingKendaraan(true);
        setKendaraanError('');
        try {
            const result = await sewaKendaraanService.getPublic();
            if (result.success) {
                setKendaraanList(result.data || []);
            } else {
                setKendaraanError(result.message || 'Gagal memuat data kendaraan');
            }
        } catch {
            setKendaraanError('Terjadi kesalahan saat memuat data');
        }
        setLoadingKendaraan(false);
    };

    // ============ SLIDER HANDLERS ============
    const handlePrevFoto = (id, total) => {
        setSliderIndex(prev => ({
            ...prev,
            [id]: ((prev[id] ?? 0) - 1 + total) % total,
        }));
    };

    const handleNextFoto = (id, total) => {
        setSliderIndex(prev => ({
            ...prev,
            [id]: ((prev[id] ?? 0) + 1) % total,
        }));
    };

    const handleGoToFoto = (id, index) => {
        setSliderIndex(prev => ({ ...prev, [id]: index }));
    };

    // ============ HELPERS ============
    const getFotoUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return getAssetUrl(path);
    };

    // Label foto dari item.fotos[{path, label}]
    const getFotoLabel = (item, index) => {
        const rawFotos = Array.isArray(item.fotos) ? item.fotos : [];
        const current = rawFotos[index];
        if (current && typeof current === 'object') return current.label || '';
        return '';
    };

    // ============ VALIDATION ============
    const validateStep = (stepNumber) => {
        const newErrors = {};

        if (stepNumber === 1) {
            if (!formData.namaLengkap) newErrors.namaLengkap = 'Nama lengkap wajib diisi';
            if (!formData.nik) newErrors.nik = 'NIK wajib diisi';
            else if (!/^[0-9]{16}$/.test(formData.nik)) newErrors.nik = 'NIK harus 16 digit angka';
            if (!formData.noTelp) newErrors.noTelp = 'Nomor telepon wajib diisi';
            else if (formData.noTelp.length < 10) newErrors.noTelp = 'Nomor telepon minimal 10 digit';
            if (!formData.alamat) newErrors.alamat = 'Alamat wajib diisi';
            else if (formData.alamat.length < 10) newErrors.alamat = 'Alamat minimal 10 karakter';
            if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
                newErrors.email = 'Format email tidak valid';
            }
        }

        if (stepNumber === 2) {
            if (!formData.kendaraanDipilih) newErrors.kendaraanDipilih = 'Kendaraan wajib dipilih';
            if (!formData.tujuanPenggunaan) newErrors.tujuanPenggunaan = 'Tujuan penggunaan wajib diisi';
            if (!formData.lokasiTujuan) newErrors.lokasiTujuan = 'Lokasi tujuan wajib diisi';
            if (!formData.tanggalMulai) newErrors.tanggalMulai = 'Tanggal mulai wajib diisi';
            if (!formData.tanggalSelesai) newErrors.tanggalSelesai = 'Tanggal selesai wajib diisi';
        }

        if (stepNumber === 3) {
            if (!formData.ktp) newErrors.ktp = 'Foto/Scan KTP wajib diupload';
            if (!formData.sim) newErrors.sim = 'SIM wajib diupload';
            if (!formData.suratPernyataan) newErrors.suratPernyataan = 'Surat pernyataan wajib diupload';
            if (!formData.termasukSupir && !formData.stnk) {
                newErrors.stnk = 'STNK wajib diupload jika tanpa supir';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ============ FORM HANDLERS ============
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleFileUpload = (e) => {
        const { name, files } = e.target;
        if (files[0]) {
            if (files[0].size > 5 * 1024 * 1024) {
                setErrors(prev => ({ ...prev, [name]: 'File maksimal 5MB' }));
                return;
            }
            setFormData(prev => ({ ...prev, [name]: files[0] }));
            if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const nextStep = () => {
        if (validateStep(step)) {
            setStep(prev => prev + 1);
            window.scrollTo(0, 0);
        }
    };

    const prevStep = () => {
        setStep(prev => prev - 1);
        window.scrollTo(0, 0);
    };

    // ============ SUBMIT KE BACKEND ============
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateStep(3)) return;

        setIsSubmitting(true);

        try {
            const fd = new FormData();

            // Data pemohon
            fd.append('nama_lengkap', formData.namaLengkap.trim());
            fd.append('nik', formData.nik.trim());
            fd.append('no_telp', formData.noTelp.trim());
            fd.append('email', formData.email.trim());
            fd.append('alamat', formData.alamat.trim());
            fd.append('pekerjaan', formData.pekerjaan.trim());
            fd.append('jenis_instansi', formData.jenisInstansi);
            fd.append('nama_instansi', formData.namaInstansi.trim());

            // Data sewa kendaraan
            fd.append('kendaraan_id', formData.kendaraanDipilih);
            fd.append('tujuan_penggunaan', formData.tujuanPenggunaan.trim());
            fd.append('lokasi_tujuan', formData.lokasiTujuan.trim());
            fd.append('tanggal_mulai', formData.tanggalMulai);
            fd.append('tanggal_selesai', formData.tanggalSelesai);
            if (formData.jumlahPenumpang) {
                fd.append('jumlah_penumpang', formData.jumlahPenumpang);
            }
            fd.append('termasuk_supir', formData.termasukSupir ? 1 : 0);

            // Dokumen
            if (formData.ktp) fd.append('file_ktp', formData.ktp);
            if (formData.sim) fd.append('file_sim', formData.sim);
            if (formData.stnk) fd.append('file_stnk', formData.stnk);
            if (formData.suratPernyataan) fd.append('file_surat_pernyataan', formData.suratPernyataan);
            if (formData.dokumenPendukung) fd.append('file_dokumen_pendukung', formData.dokumenPendukung);

            // Honeypot
            fd.append('website_url', formData.website_url);

            const result = await sewaKendaraanService.submit(fd);

            if (result.success) {
                setNomorRegistrasi(result.data.nomor_registrasi);
                setShowSuccess(true);
                window.scrollTo(0, 0);
            } else {
                if (result.errors) {
                    const mapped = {};
                    Object.entries(result.errors).forEach(([k, v]) => {
                        mapped[k] = Array.isArray(v) ? v[0] : v;
                    });
                    setErrors(mapped);
                }
                alert(result.message || 'Gagal mengirim permohonan');
            }
        } catch (err) {
            console.error(err);
            alert('Terjadi kesalahan. Silakan coba lagi.');
        }

        setIsSubmitting(false);
    };

    // ============ ESTIMASI BIAYA ============
    const hitungEstimasiBiaya = () => {
        if (!formData.kendaraanDipilih || !formData.tanggalMulai || !formData.tanggalSelesai) return null;

        const kendaraan = kendaraanList.find(k => String(k.id) === String(formData.kendaraanDipilih));
        if (!kendaraan) return null;

        const tglMulai = new Date(formData.tanggalMulai);
        const tglSelesai = new Date(formData.tanggalSelesai);
        const diffTime = Math.abs(tglSelesai - tglMulai);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        const tarifPerHari = formData.termasukSupir
            ? Number(kendaraan.tarif_dengan_supir || kendaraan.tarif_per_hari || 0)
            : Number(kendaraan.tarif_per_hari || 0);

        const biayaSewa = diffDays * tarifPerHari;
        const deposit = biayaSewa * 0.25;
        const biayaAdmin = 75000;

        return {
            nama: kendaraan.nama,
            diffDays,
            tarifPerHari,
            biayaSewa,
            deposit,
            biayaAdmin,
            total: biayaSewa + deposit + biayaAdmin,
        };
    };

    // ============ STEP INDICATOR ============
    const renderStepIndicator = () => (
        <div className="flex items-center justify-center mb-8">
            {[
                { step: 1, label: 'Data Pemohon', icon: FaUser },
                { step: 2, label: 'Data Sewa', icon: FaCalendarAlt },
                { step: 3, label: 'Upload Dokumen', icon: FaUpload },
            ].map((item, index) => (
                <React.Fragment key={item.step}>
                    <div className="flex flex-col items-center">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold transition-all duration-300 ${step >= item.step ? 'bg-[#0D9488] text-white shadow-lg' : 'bg-gray-300 text-gray-600'
                            }`}>
                            {step > item.step ? <FaCheckCircle /> : <item.icon />}
                        </div>
                        <span className={`mt-2 text-xs font-medium hidden md:block ${step >= item.step ? 'text-[#0D9488]' : 'text-gray-500'
                            }`}>
                            {item.label}
                        </span>
                    </div>
                    {index < 2 && (
                        <div className={`w-16 md:w-24 h-1 mx-2 transition-all duration-300 ${step > item.step ? 'bg-[#0D9488]' : 'bg-gray-300'
                            }`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );

    // ============ SUCCESS PAGE ============
    if (showSuccess) {
        const estimasi = hitungEstimasiBiaya();
        return (
            <div className="container mx-auto px-4 py-12">
                <div className="max-w-2xl mx-auto">
                    <div className="card-glass text-center">
                        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                            <FaCheckCircle className="text-6xl text-green-500" />
                        </div>
                        <h2 className="text-3xl font-bold text-[#1E3A8A] mb-4">
                            Pengajuan Sewa Berhasil!
                        </h2>
                        <p className="text-gray-600 mb-6">
                            Terima kasih telah mengajukan permohonan sewa kendaraan.
                            Berikut adalah nomor registrasi Anda:
                        </p>

                        <div className="bg-gradient-to-r from-[#1E3A8A] to-[#0D9488] text-white p-6 rounded-lg mb-6">
                            <p className="text-sm opacity-80 mb-2">Nomor Registrasi</p>
                            <p className="text-3xl font-bold font-mono">{nomorRegistrasi}</p>
                        </div>

                        {estimasi && (
                            <div className="bg-gray-50 p-6 rounded-lg mb-6 text-left">
                                <h3 className="font-bold text-[#1E3A8A] mb-3 flex items-center">
                                    <FaMoneyBillWave className="mr-2" />
                                    Estimasi Biaya Sewa
                                </h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Kendaraan:</span>
                                        <span className="font-semibold">{estimasi.nama}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Durasi:</span>
                                        <span className="font-semibold">{estimasi.diffDays} hari</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Biaya Sewa:</span>
                                        <span>Rp {estimasi.biayaSewa.toLocaleString('id-ID')}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Deposit (25%):</span>
                                        <span>Rp {estimasi.deposit.toLocaleString('id-ID')}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Biaya Admin:</span>
                                        <span>Rp {estimasi.biayaAdmin.toLocaleString('id-ID')}</span>
                                    </div>
                                    <hr />
                                    <div className="flex justify-between text-lg font-bold text-[#0D9488]">
                                        <span>Estimasi Total:</span>
                                        <span>Rp {estimasi.total.toLocaleString('id-ID')}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="text-left bg-blue-50 p-6 rounded-lg mb-6">
                            <h3 className="font-bold text-blue-800 mb-3 flex items-center">
                                <FaClipboardList className="mr-2" />
                                Informasi Selanjutnya:
                            </h3>
                            <ul className="space-y-3 text-sm text-blue-700">
                                <li className="flex items-start">
                                    <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                                    <span>Simpan nomor registrasi untuk cek status permohonan</span>
                                </li>
                                <li className="flex items-start">
                                    <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                                    <span>Petugas akan menghubungi Anda dalam 1-2 hari kerja</span>
                                </li>
                                <li className="flex items-start">
                                    <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                                    <span>Pembayaran dilakukan setelah permohonan disetujui</span>
                                </li>
                            </ul>
                        </div>

                        <div className="flex flex-col md:flex-row gap-4">
                            <button
                                onClick={() => window.print()}
                                className="flex-1 bg-[#0F172A] text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors flex items-center justify-center"
                            >
                                <FaPrint className="mr-2" /> Cetak Bukti
                            </button>
                            <button
                                onClick={() => {
                                    setShowSuccess(false);
                                    setStep(1);
                                    setShowForm(false);
                                    setFormData({
                                        namaLengkap: '', nik: '', noTelp: '', email: '', alamat: '',
                                        pekerjaan: '', jenisInstansi: 'perseorangan', namaInstansi: '',
                                        kendaraanDipilih: '', tujuanPenggunaan: '', lokasiTujuan: '',
                                        jumlahPenumpang: '', termasukSupir: false,
                                        tanggalMulai: '', tanggalSelesai: '',
                                        ktp: null, sim: null, stnk: null,
                                        suratPernyataan: null, dokumenPendukung: null,
                                        website_url: '',
                                    });
                                }}
                                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors flex items-center justify-center"
                            >
                                <FaArrowLeft className="mr-2" /> Ajukan Sewa Baru
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ============ MAIN RENDER ============
    return (
        <div className="container mx-auto px-4 py-12">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center space-x-3 mb-2">
                    <FaCar className="text-4xl text-[#0D9488]" />
                    <h1 className="text-3xl md:text-4xl font-bold text-[#1E3A8A]">
                        Penyewaan Kendaraan Dinas
                    </h1>
                </div>
                <p className="text-gray-600 text-lg ml-12">
                    Layanan sewa kendaraan aset Pemerintah Kota Magelang
                </p>
            </div>

            {/* Category */}
            <div className="flex flex-wrap gap-4 mb-8">
                {categories.map((cat) => (
                    <button
                        key={cat.id}
                        onClick={() => {
                            setActiveCategory(cat.id);
                            setShowForm(false);
                            setStep(1);
                        }}
                        className={`flex items-center px-6 py-4 rounded-xl font-semibold transition-all duration-300 ${activeCategory === cat.id
                                ? 'bg-[#1E3A8A] text-white shadow-lg transform scale-105'
                                : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                            }`}
                    >
                        <cat.icon className="mr-2 text-xl" />
                        {cat.label}
                    </button>
                ))}
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-200">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => {
                            setActiveTab(tab.id);
                            if (tab.id !== 'formulir') setShowForm(false);
                            setStep(1);
                        }}
                        className={`flex items-center px-6 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${activeTab === tab.id
                                ? 'border-[#0D9488] text-[#0D9488] bg-[#0D9488]/5'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <tab.icon className="mr-2" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ============ DAFTAR ASET ============ */}
            {activeTab === 'daftar' && (
                <div>
                    {loadingKendaraan ? (
                        <div className="text-center py-20">
                            <FaSpinner className="animate-spin text-4xl text-[#0D9488] mx-auto mb-3" />
                            <p className="text-gray-500">Memuat data kendaraan...</p>
                        </div>
                    ) : kendaraanError ? (
                        <div className="text-center py-20">
                            <FaExclamationTriangle className="text-5xl text-yellow-500 mx-auto mb-3" />
                            <p className="text-red-500">{kendaraanError}</p>
                        </div>
                    ) : kendaraanList.length === 0 ? (
                        <div className="text-center py-20">
                            <FaCar className="text-6xl text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Belum ada kendaraan tersedia</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {kendaraanList.map((item) => {
                                const fotos = item.foto_urls || [];
                                const currentIdx = sliderIndex[item.id] ?? 0;
                                const currentFoto = fotos[currentIdx];
                                const hasMultiple = fotos.length > 1;
                                const label = getFotoLabel(item, currentIdx);

                                return (
                                    <div
                                        key={item.id}
                                        className={`card-glass overflow-hidden ${!item.tersedia ? 'opacity-60' : ''}`}
                                    >
                                        {/* ✅ SLIDER FOTO */}
                                        <div className="relative h-56 bg-gradient-to-br from-[#1E3A8A] to-[#0D9488] flex items-center justify-center text-white overflow-hidden group">
                                            {currentFoto ? (
                                                <img
                                                    src={getFotoUrl(currentFoto)}
                                                    alt={item.nama}
                                                    className="w-full h-full object-cover transition-opacity duration-300"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        const fallback = e.target.parentNode.querySelector('.emoji-fallback');
                                                        if (fallback) fallback.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}

                                            {/* ✅ Label overlay */}
                                            {label && (
                                                <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-lg max-w-[70%] truncate z-20">
                                                    {label}
                                                </div>
                                            )}

                                            {/* Fallback emoji */}
                                            <span
                                                className="emoji-fallback text-6xl absolute"
                                                style={{ display: currentFoto ? 'none' : 'flex' }}
                                            >
                                                {item.foto_emoji || '🚗'}
                                            </span>

                                            {/* Navigasi slider */}
                                            {hasMultiple && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => handlePrevFoto(item.id, fotos.length)}
                                                        className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                                        aria-label="Sebelumnya"
                                                    >
                                                        <FaChevronLeft size={12} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleNextFoto(item.id, fotos.length)}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                                        aria-label="Berikutnya"
                                                    >
                                                        <FaChevronRight size={12} />
                                                    </button>

                                                    {/* Counter */}
                                                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full z-10">
                                                        {currentIdx + 1} / {fotos.length}
                                                    </div>

                                                    {/* Dots */}
                                                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                                                        {fotos.map((_, i) => (
                                                            <button
                                                                key={i}
                                                                type="button"
                                                                onClick={() => handleGoToFoto(item.id, i)}
                                                                className={`rounded-full transition-all ${currentIdx === i
                                                                        ? 'bg-white w-4 h-1.5'
                                                                        : 'bg-white/50 hover:bg-white/80 w-1.5 h-1.5'
                                                                    }`}
                                                                aria-label={`Foto ${i + 1}`}
                                                            />
                                                        ))}
                                                    </div>
                                                </>
                                            )}

                                            {/* Status badge */}
                                            {!item.tersedia && (
                                                <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full z-10">
                                                    Tidak Tersedia
                                                </div>
                                            )}

                                            {/* Foto count */}
                                            {fotos.length > 1 && (
                                                <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-1 rounded-full flex items-center gap-1 z-10">
                                                    <FaImage size={9} /> {fotos.length}
                                                </div>
                                            )}
                                        </div>

                                        {/* ✅ Thumbnail strip */}
                                        {fotos.length > 1 && (
                                            <div className="flex gap-1 px-3 pt-3 overflow-x-auto scrollbar-thin">
                                                {fotos.map((f, i) => (
                                                    <button
                                                        key={i}
                                                        type="button"
                                                        onClick={() => handleGoToFoto(item.id, i)}
                                                        className={`flex-shrink-0 w-12 h-12 rounded overflow-hidden border-2 transition-all ${currentIdx === i
                                                                ? 'border-[#0D9488] scale-105'
                                                                : 'border-gray-200 opacity-60 hover:opacity-100'
                                                            }`}
                                                    >
                                                        <img
                                                            src={getFotoUrl(f)}
                                                            alt={`thumb-${i}`}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                e.target.src = 'https://via.placeholder.com/50?text=?';
                                                            }}
                                                        />
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {/* Konten */}
                                        <div className="p-6">
                                            <h3 className="text-xl font-bold text-[#1E3A8A] mb-2">{item.nama}</h3>
                                            {item.tipe && (
                                                <p className="text-sm text-gray-500 mb-2">{item.tipe}</p>
                                            )}
                                            <p className="text-sm text-gray-600 mb-3">{item.deskripsi}</p>

                                            <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                                                <div className="bg-gray-50 p-2 rounded">
                                                    <p className="font-semibold">{item.kapasitas || '-'}</p>
                                                    <p className="text-xs text-gray-500">Kapasitas</p>
                                                </div>
                                                <div className="bg-gray-50 p-2 rounded">
                                                    <p className="font-semibold">{item.tahun || 'Baik'}</p>
                                                    <p className="text-xs text-gray-500">
                                                        {item.tahun ? 'Tahun' : 'Kondisi'}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <p className="text-sm font-semibold text-gray-700 mb-2">Fasilitas:</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {Array.isArray(item.fasilitas) && item.fasilitas.map((fas, idx) => (
                                                        <span key={idx} className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full">
                                                            {fas}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="border-t pt-4">
                                                <div className="flex justify-between items-center mb-3">
                                                    <div>
                                                        <p className="text-lg font-bold text-[#0D9488]">
                                                            Rp {Number(item.tarif_per_hari).toLocaleString('id-ID')}
                                                        </p>
                                                        <p className="text-xs text-gray-500">per hari</p>
                                                    </div>
                                                    {item.tarif_dengan_supir && (
                                                        <div>
                                                            <p className="text-sm font-semibold text-[#1E3A8A]">
                                                                Rp {Number(item.tarif_dengan_supir).toLocaleString('id-ID')}
                                                            </p>
                                                            <p className="text-xs text-gray-500">dengan supir</p>
                                                        </div>
                                                    )}
                                                </div>

                                                <button
                                                    onClick={() => {
                                                        setActiveTab('formulir');
                                                        setShowForm(true);
                                                        setFormData(prev => ({ ...prev, kendaraanDipilih: item.id }));
                                                        window.scrollTo(0, 0);
                                                    }}
                                                    disabled={!item.tersedia}
                                                    className={`w-full py-2 rounded-lg font-semibold transition-colors ${item.tersedia
                                                            ? 'btn-primary'
                                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                        }`}
                                                >
                                                    {item.tersedia ? 'Sewa Sekarang' : 'Tidak Tersedia'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ============ FORMULIR SEWA ============ */}
            {activeTab === 'formulir' && (
                <div>
                    {!showForm ? (
                        <div className="text-center py-12">
                            <FaCar className="text-6xl text-gray-300 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-gray-500 mb-2">
                                Pilih Kendaraan Terlebih Dahulu
                            </h3>
                            <p className="text-gray-400 mb-6">
                                Silakan pilih kendaraan yang ingin disewa dari daftar
                            </p>
                            <button onClick={() => setActiveTab('daftar')} className="btn-primary">
                                Lihat Daftar Aset
                            </button>
                        </div>
                    ) : (
                        <div>
                            {renderStepIndicator()}

                            <div className="max-w-4xl mx-auto">
                                <div className="card-glass">
                                    <form onSubmit={handleSubmit}>
                                        {/* 🍯 Honeypot */}
                                        <input
                                            type="text"
                                            name="website_url"
                                            value={formData.website_url}
                                            onChange={handleInputChange}
                                            style={{ display: 'none', position: 'absolute', left: '-9999px' }}
                                            tabIndex="-1"
                                            autoComplete="off"
                                            aria-hidden="true"
                                        />

                                        {/* ============ STEP 1 ============ */}
                                        {step === 1 && (
                                            <div className="space-y-6">
                                                <div className="flex items-center pb-4 border-b border-gray-200">
                                                    <div className="w-12 h-12 bg-[#0D9488]/10 rounded-full flex items-center justify-center mr-3">
                                                        <FaUser className="text-xl text-[#0D9488]" />
                                                    </div>
                                                    <div>
                                                        <h2 className="text-xl font-bold text-[#1E3A8A]">
                                                            Data Pemohon
                                                        </h2>
                                                        <p className="text-sm text-gray-500">
                                                            Lengkapi data diri pemohon sewa
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Nama Lengkap <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="namaLengkap"
                                                            value={formData.namaLengkap}
                                                            onChange={handleInputChange}
                                                            maxLength={255}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${errors.namaLengkap ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Masukkan nama lengkap"
                                                        />
                                                        {errors.namaLengkap && (
                                                            <p className="text-red-500 text-xs mt-1">{errors.namaLengkap}</p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            NIK <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="nik"
                                                            value={formData.nik}
                                                            onChange={(e) => {
                                                                const val = e.target.value.replace(/\D/g, '').slice(0, 16);
                                                                setFormData(prev => ({ ...prev, nik: val }));
                                                                if (errors.nik) setErrors(prev => ({ ...prev, nik: '' }));
                                                            }}
                                                            maxLength={16}
                                                            inputMode="numeric"
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${errors.nik ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Masukkan 16 digit NIK"
                                                        />
                                                        <p className="text-xs text-gray-400 mt-1">{formData.nik.length}/16 digit</p>
                                                        {errors.nik && (
                                                            <p className="text-red-500 text-xs mt-1">{errors.nik}</p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Nomor Telepon <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="tel"
                                                            name="noTelp"
                                                            value={formData.noTelp}
                                                            onChange={handleInputChange}
                                                            maxLength={20}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${errors.noTelp ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                                }`}
                                                            placeholder="08123456789"
                                                        />
                                                        {errors.noTelp && (
                                                            <p className="text-red-500 text-xs mt-1">{errors.noTelp}</p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Email
                                                        </label>
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            value={formData.email}
                                                            onChange={handleInputChange}
                                                            maxLength={255}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                                }`}
                                                            placeholder="contoh@email.com"
                                                        />
                                                        {errors.email && (
                                                            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                                                        )}
                                                    </div>

                                                    <div className="md:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Alamat Lengkap <span className="text-red-500">*</span>
                                                        </label>
                                                        <textarea
                                                            name="alamat"
                                                            value={formData.alamat}
                                                            onChange={handleInputChange}
                                                            rows="3"
                                                            maxLength={1000}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${errors.alamat ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Masukkan alamat lengkap sesuai KTP"
                                                        />
                                                        {errors.alamat && (
                                                            <p className="text-red-500 text-xs mt-1">{errors.alamat}</p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Pekerjaan
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="pekerjaan"
                                                            value={formData.pekerjaan}
                                                            onChange={handleInputChange}
                                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent"
                                                            placeholder="Masukkan pekerjaan"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Jenis Instansi
                                                        </label>
                                                        <select
                                                            name="jenisInstansi"
                                                            value={formData.jenisInstansi}
                                                            onChange={handleInputChange}
                                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent"
                                                        >
                                                            <option value="perseorangan">Perseorangan</option>
                                                            <option value="perusahaan">Perusahaan</option>
                                                            <option value="instansi">Instansi Pemerintah</option>
                                                            <option value="organisasi">Organisasi/Komunitas</option>
                                                        </select>
                                                    </div>

                                                    {formData.jenisInstansi !== 'perseorangan' && (
                                                        <div className="md:col-span-2">
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Nama Instansi
                                                            </label>
                                                            <input
                                                                type="text"
                                                                name="namaInstansi"
                                                                value={formData.namaInstansi}
                                                                onChange={handleInputChange}
                                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent"
                                                                placeholder="Masukkan nama instansi"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* ============ STEP 2 ============ */}
                                        {step === 2 && (
                                            <div className="space-y-6">
                                                <div className="flex items-center pb-4 border-b border-gray-200">
                                                    <div className="w-12 h-12 bg-[#0D9488]/10 rounded-full flex items-center justify-center mr-3">
                                                        <FaCalendarAlt className="text-xl text-[#0D9488]" />
                                                    </div>
                                                    <div>
                                                        <h2 className="text-xl font-bold text-[#1E3A8A]">
                                                            Data Penyewaan
                                                        </h2>
                                                        <p className="text-sm text-gray-500">
                                                            Informasi kendaraan dan jadwal sewa
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div className="md:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Kendaraan <span className="text-red-500">*</span>
                                                        </label>
                                                        <select
                                                            name="kendaraanDipilih"
                                                            value={formData.kendaraanDipilih}
                                                            onChange={handleInputChange}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${errors.kendaraanDipilih ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                        >
                                                            <option value="">Pilih Kendaraan</option>
                                                            {kendaraanList
                                                                .filter(k => k.tersedia)
                                                                .map(k => (
                                                                    <option key={k.id} value={k.id}>
                                                                        {k.nama} {k.tipe ? `- ${k.tipe}` : ''}
                                                                    </option>
                                                                ))}
                                                        </select>
                                                        {errors.kendaraanDipilih && (
                                                            <p className="text-red-500 text-xs mt-1">{errors.kendaraanDipilih}</p>
                                                        )}
                                                    </div>

                                                    <div className="md:col-span-2">
                                                        <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                name="termasukSupir"
                                                                checked={formData.termasukSupir}
                                                                onChange={handleInputChange}
                                                                className="mr-2 text-[#0D9488] focus:ring-[#0D9488]"
                                                            />
                                                            <span className="text-sm font-medium text-gray-700">
                                                                Termasuk Supir
                                                            </span>
                                                        </label>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Tujuan Penggunaan <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="tujuanPenggunaan"
                                                            value={formData.tujuanPenggunaan}
                                                            onChange={handleInputChange}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${errors.tujuanPenggunaan ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Contoh: Perjalanan dinas, study tour"
                                                        />
                                                        {errors.tujuanPenggunaan && (
                                                            <p className="text-red-500 text-xs mt-1">{errors.tujuanPenggunaan}</p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Lokasi Tujuan <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="lokasiTujuan"
                                                            value={formData.lokasiTujuan}
                                                            onChange={handleInputChange}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${errors.lokasiTujuan ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Masukkan kota/kabupaten tujuan"
                                                        />
                                                        {errors.lokasiTujuan && (
                                                            <p className="text-red-500 text-xs mt-1">{errors.lokasiTujuan}</p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Jumlah Penumpang
                                                        </label>
                                                        <input
                                                            type="number"
                                                            name="jumlahPenumpang"
                                                            value={formData.jumlahPenumpang}
                                                            onChange={handleInputChange}
                                                            min="1"
                                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent"
                                                            placeholder="Estimasi jumlah penumpang"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Tanggal Mulai <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="date"
                                                            name="tanggalMulai"
                                                            value={formData.tanggalMulai}
                                                            onChange={handleInputChange}
                                                            min={new Date().toISOString().split('T')[0]}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${errors.tanggalMulai ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                        />
                                                        {errors.tanggalMulai && (
                                                            <p className="text-red-500 text-xs mt-1">{errors.tanggalMulai}</p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Tanggal Selesai <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="date"
                                                            name="tanggalSelesai"
                                                            value={formData.tanggalSelesai}
                                                            onChange={handleInputChange}
                                                            min={
                                                                formData.tanggalMulai ||
                                                                new Date().toISOString().split('T')[0]
                                                            }
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent ${errors.tanggalSelesai ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                        />
                                                        {errors.tanggalSelesai && (
                                                            <p className="text-red-500 text-xs mt-1">{errors.tanggalSelesai}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Estimasi Biaya */}
                                                {hitungEstimasiBiaya() && (
                                                    <div className="bg-green-50 p-4 rounded-lg">
                                                        <h4 className="font-semibold text-green-800 mb-2">
                                                            Estimasi Biaya
                                                        </h4>
                                                        <div className="space-y-1 text-sm">
                                                            {(() => {
                                                                const est = hitungEstimasiBiaya();
                                                                return (
                                                                    <>
                                                                        <div className="flex justify-between">
                                                                            <span>Sewa {est.diffDays} hari:</span>
                                                                            <span>Rp {est.biayaSewa.toLocaleString('id-ID')}</span>
                                                                        </div>
                                                                        <div className="flex justify-between">
                                                                            <span>Deposit (25%):</span>
                                                                            <span>Rp {est.deposit.toLocaleString('id-ID')}</span>
                                                                        </div>
                                                                        <div className="flex justify-between">
                                                                            <span>Biaya Admin:</span>
                                                                            <span>Rp {est.biayaAdmin.toLocaleString('id-ID')}</span>
                                                                        </div>
                                                                        <hr className="border-green-200" />
                                                                        <div className="flex justify-between font-bold">
                                                                            <span>Total Estimasi:</span>
                                                                            <span>Rp {est.total.toLocaleString('id-ID')}</span>
                                                                        </div>
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* ============ STEP 3 ============ */}
                                        {step === 3 && (
                                            <div className="space-y-6">
                                                <div className="flex items-center pb-4 border-b border-gray-200">
                                                    <div className="w-12 h-12 bg-[#0D9488]/10 rounded-full flex items-center justify-center mr-3">
                                                        <FaUpload className="text-xl text-[#0D9488]" />
                                                    </div>
                                                    <div>
                                                        <h2 className="text-xl font-bold text-[#1E3A8A]">
                                                            Upload Dokumen
                                                        </h2>
                                                        <p className="text-sm text-gray-500">
                                                            Lengkapi dokumen persyaratan sewa
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="bg-yellow-50 p-4 rounded-lg flex items-start">
                                                    <FaInfoCircle className="text-yellow-600 mt-1 mr-3 flex-shrink-0" />
                                                    <div>
                                                        <p className="text-sm font-semibold text-yellow-800 mb-1">
                                                            Ketentuan Upload:
                                                        </p>
                                                        <ul className="text-sm text-yellow-700 list-disc list-inside">
                                                            <li>Format: PDF, JPG, PNG (Maks. 5MB/file)</li>
                                                            <li>
                                                                Tanda <span className="text-red-500">*</span> wajib diupload
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    {/* KTP */}
                                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <label className="text-sm font-medium text-gray-700">
                                                                    Foto/Scan KTP <span className="text-red-500">*</span>
                                                                </label>
                                                                {formData.ktp && (
                                                                    <p className="text-sm text-green-600 mt-1 truncate">
                                                                        ✓ {formData.ktp.name} ({(formData.ktp.size / 1024).toFixed(1)} KB)
                                                                    </p>
                                                                )}
                                                                {errors.ktp && (
                                                                    <p className="text-red-500 text-xs mt-1">{errors.ktp}</p>
                                                                )}
                                                            </div>
                                                            <label className="cursor-pointer bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm hover:bg-gray-50 transition-colors">
                                                                Pilih File
                                                                <input
                                                                    type="file"
                                                                    name="ktp"
                                                                    onChange={handleFileUpload}
                                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                                    className="hidden"
                                                                />
                                                            </label>
                                                        </div>
                                                    </div>

                                                    {/* SIM */}
                                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <label className="text-sm font-medium text-gray-700">
                                                                    SIM Aktif <span className="text-red-500">*</span>
                                                                </label>
                                                                {formData.sim && (
                                                                    <p className="text-sm text-green-600 mt-1 truncate">
                                                                        ✓ {formData.sim.name} ({(formData.sim.size / 1024).toFixed(1)} KB)
                                                                    </p>
                                                                )}
                                                                {errors.sim && (
                                                                    <p className="text-red-500 text-xs mt-1">{errors.sim}</p>
                                                                )}
                                                            </div>
                                                            <label className="cursor-pointer bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm hover:bg-gray-50 transition-colors">
                                                                Pilih File
                                                                <input
                                                                    type="file"
                                                                    name="sim"
                                                                    onChange={handleFileUpload}
                                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                                    className="hidden"
                                                                />
                                                            </label>
                                                        </div>
                                                    </div>

                                                    {/* STNK (jika tanpa supir) */}
                                                    {!formData.termasukSupir && (
                                                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                                            <div className="flex items-center justify-between flex-wrap gap-2">
                                                                <div className="flex-1 min-w-0">
                                                                    <label className="text-sm font-medium text-gray-700">
                                                                        STNK Kendaraan <span className="text-red-500">*</span>
                                                                    </label>
                                                                    {formData.stnk && (
                                                                        <p className="text-sm text-green-600 mt-1 truncate">
                                                                            ✓ {formData.stnk.name} ({(formData.stnk.size / 1024).toFixed(1)} KB)
                                                                        </p>
                                                                    )}
                                                                    {errors.stnk && (
                                                                        <p className="text-red-500 text-xs mt-1">{errors.stnk}</p>
                                                                    )}
                                                                </div>
                                                                <label className="cursor-pointer bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm hover:bg-gray-50 transition-colors">
                                                                    Pilih File
                                                                    <input
                                                                        type="file"
                                                                        name="stnk"
                                                                        onChange={handleFileUpload}
                                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                                        className="hidden"
                                                                    />
                                                                </label>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Surat Pernyataan */}
                                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <label className="text-sm font-medium text-gray-700">
                                                                    Surat Pernyataan <span className="text-red-500">*</span>
                                                                </label>
                                                                {formData.suratPernyataan && (
                                                                    <p className="text-sm text-green-600 mt-1 truncate">
                                                                        ✓ {formData.suratPernyataan.name} ({(formData.suratPernyataan.size / 1024).toFixed(1)} KB)
                                                                    </p>
                                                                )}
                                                                {errors.suratPernyataan && (
                                                                    <p className="text-red-500 text-xs mt-1">{errors.suratPernyataan}</p>
                                                                )}
                                                            </div>
                                                            <label className="cursor-pointer bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm hover:bg-gray-50 transition-colors">
                                                                Pilih File
                                                                <input
                                                                    type="file"
                                                                    name="suratPernyataan"
                                                                    onChange={handleFileUpload}
                                                                    accept=".pdf"
                                                                    className="hidden"
                                                                />
                                                            </label>
                                                        </div>
                                                    </div>

                                                    {/* Dokumen Pendukung */}
                                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <label className="text-sm font-medium text-gray-700">
                                                                    Dokumen Pendukung Lainnya (Opsional)
                                                                </label>
                                                                {formData.dokumenPendukung && (
                                                                    <p className="text-sm text-green-600 mt-1 truncate">
                                                                        ✓ {formData.dokumenPendukung.name} ({(formData.dokumenPendukung.size / 1024).toFixed(1)} KB)
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <label className="cursor-pointer bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm hover:bg-gray-50 transition-colors">
                                                                Pilih File
                                                                <input
                                                                    type="file"
                                                                    name="dokumenPendukung"
                                                                    onChange={handleFileUpload}
                                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                                    className="hidden"
                                                                />
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Navigation */}
                                        <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                                            {step > 1 ? (
                                                <button
                                                    type="button"
                                                    onClick={prevStep}
                                                    className="flex items-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                                >
                                                    <FaArrowLeft className="mr-2" /> Sebelumnya
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowForm(false)}
                                                    className="flex items-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                                >
                                                    <FaArrowLeft className="mr-2" /> Kembali
                                                </button>
                                            )}

                                            {step < 3 ? (
                                                <button
                                                    type="button"
                                                    onClick={nextStep}
                                                    className="flex items-center btn-primary"
                                                >
                                                    Selanjutnya <FaArrowRight className="ml-2" />
                                                </button>
                                            ) : (
                                                <button
                                                    type="submit"
                                                    disabled={isSubmitting}
                                                    className={`flex items-center btn-primary ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <FaSpinner className="animate-spin mr-2" />
                                                            Mengirim...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaPaperPlane className="mr-2" />
                                                            Kirim Permohonan Sewa
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ============ CEK STATUS ============ */}
            {activeTab === 'status' && (
                <div className="max-w-2xl mx-auto">
                    <div className="card-glass">
                        <h3 className="text-xl font-bold text-[#1E3A8A] mb-4 flex items-center">
                            <FaSearch className="mr-2 text-[#0D9488]" />
                            Cek Status Permohonan Sewa
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nomor Registrasi
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent"
                                    placeholder="SEWA-KDR-2026-00001"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">NIK</label>
                                <input
                                    type="text"
                                    maxLength={16}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-transparent"
                                    placeholder="Masukkan 16 digit NIK"
                                />
                            </div>
                            <button className="btn-primary w-full">
                                <FaSearch className="mr-2 inline" /> Cek Status
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SewaBMD;